import React from 'react';

import EllipsisTextHint from '@pb/kit-component-ellipsis-text-hint';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {Button} from '../button';

import styles from './filter-pill.component.module.styl';

import type {ButtonProps} from '../button';
import type {ReactNode} from 'react';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

export interface FilterPillProps extends Omit<ButtonProps, 'variant'> {
    /**
     * Badge value rendered in parentheses after the label (e.g. `"(3)"`).
     * Typically the number of active filter selections this pill represents.
     * Omit to show the label without a count.
     */
    count?: ReactNode,
    /**
     * When `true`, skips the `EllipsisTextHint` wrapper and applies a plain
     * CSS `overflow: hidden; text-overflow: ellipsis` to the label. Use when
     * the native browser tooltip on overflow is undesirable.
     */
    disableBrowserHint?: boolean,
    /**
     * When `true`, applies the selected/active visual style. Prefer the native
     * `aria-pressed` prop — it sets the same CSS state and also satisfies
     * accessibility requirements for toggle buttons.
     *
     * @deprecated It will be removed in 6 months 10-02-2026. Use aria-pressed instead.
     */
    selected?: boolean,
}

/**
 * Toggle button styled as a filter pill, with an optional active count badge.
 *
 * Built on top of `Button` with `variant="Type.SECONDARY"`. Renders the label
 * text with `EllipsisTextHint` for single-line overflow truncation (disable via
 * `disableBrowserHint`). The `selected` or `aria-pressed` prop drives the
 * active CSS state and ARIA toggle semantics.
 *
 * ## Accessibility
 * - `aria-pressed` indicates the toggle state to screen readers.
 * - `aria-disabled` is propagated from the native `disabled` prop.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/buttons-button-filter-pill--documentation}
 */

export const FilterPill = (props: FilterPillProps) => {
    const {
        children,
        className,
        count,
        disableBrowserHint,
        disabled,
        selected,
        ...buttonProps
    } = props;

    const filterButtonClassName = classNames(
        'filter-pill', 'filter-pill__icon',
        className,
        {
            'filter-pill_selected': props['aria-pressed'] || selected,
            'filter-pill_disabled': disabled,
        },
    );

    const pillName = (
        <span>
            {children}
            {count ? <span className={classes['filter-pill__count']}>({count})</span> : null}
        </span>
    );

    return (
        <Button
            disabled={disabled}
            aria-pressed={selected}
            {...buttonProps}
            variant="Type.SECONDARY"
            className={filterButtonClassName}>
            {disableBrowserHint ? (
                <span className={classNames('filter-pill__name ellipsis')}>{pillName}</span>
            ) : (
                <EllipsisTextHint mix={classes.pill__name}>{pillName}</EllipsisTextHint>
            )}
        </Button>
    );
};

FilterPill.displayName = 'FilterPill';
